import React, { useState, useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Importera Supabase 
import { supabase } from './supabaseClient';
import { LanguageProvider } from './LanguageContext';

// Light pages - load normally
import Login from './Login';
import ResetPassword from './ResetPassword';
import SignupPage from './SignupPage';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import NotFound from './NotFound';

import SEO from './components/SEO';
import CookieBanner from './components/CookieBanner';
import Analytics from './components/Analytics';

// Lazy load heavy pages
const LandingPage = lazy(() => import('./LandingPage'));
const Dashboard = lazy(() => import('./Dashboard'));
const HowItWorks = lazy(() => import('./HowItWorks'));
const AdminPanel = lazy(() => import('./AdminPanel'));
const FAQ = lazy(() => import('./FAQ'));
const OnboardingWizard = lazy(() => import('./components/onboarding/OnboardingWizard'));

// Lazy load guide pages
const SecurityChecklist = lazy(() => import('./guides/SecurityChecklist'));
const DarkWebGuide = lazy(() => import('./guides/DarkWebGuide'));
const GDPRGuide = lazy(() => import('./guides/GDPRGuide'));
const PhishingGuide = lazy(() => import('./guides/PhishingGuide'));
const PasswordGuide = lazy(() => import('./guides/PasswordGuide'));
const VPNGuide = lazy(() => import('./guides/VPNGuide'));
const BackupGuide = lazy(() => import('./guides/BackupGuide'));

// Loading fallback component
const PageLoader = () => (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
            <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Laddar...</p>
        </div>
    </div>
);

const App = () => {
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [onboardingCompleted, setOnboardingCompleted] = useState(null); // null = unknown, true/false = known


    useEffect(() => {
        // Hämta session direkt med timeout fallback
        const sessionTimeout = setTimeout(() => {
            setLoading(false); // Don't block app if Supabase is slow/unreachable
        }, 3000);

        const checkOnboardingStatus = async (userId) => {
            try {
                const { data: profile } = await supabase
                    .from('profiles')
                    .select('onboarding_completed')
                    .eq('id', userId)
                    .single();
                setOnboardingCompleted(profile?.onboarding_completed === true);
            } catch {
                setOnboardingCompleted(false); // Default to false on error (profile doesn't exist yet)
            }
        };

        supabase.auth.getSession().then(({ data: { session } }) => {
            clearTimeout(sessionTimeout);
            setSession(session);
            if (session?.user?.id) {
                checkOnboardingStatus(session.user.id).then(() => setLoading(false));
            } else {
                setOnboardingCompleted(null);
                setLoading(false);
            }
        }).catch(() => {
            clearTimeout(sessionTimeout);
            setLoading(false); // Gracefully handle Supabase errors
        });

        // Lyssna på förändringar
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session);
            if (session?.user?.id) {
                checkOnboardingStatus(session.user.id);
            } else {
                setOnboardingCompleted(null);
            }
        });

        return () => {
            clearTimeout(sessionTimeout);
            subscription.unsubscribe();
        };
    }, []);


    const handleLogout = async () => {
        await supabase.auth.signOut();
    };

    if (loading) {
        return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">Laddar...</div>;
    }

    return (
        <LanguageProvider>
            <Router>
                <SEO />
                <CookieBanner />
                <Analytics />
                <Suspense fallback={<PageLoader />}>
                    <Routes>
                        <Route path="/" element={
                            session ? (
                                onboardingCompleted ? (
                                    <Navigate to="/dashboard" replace />
                                ) : (
                                    <Navigate to="/onboarding" replace />
                                )
                            ) : (
                                <LandingPage />
                            )
                        } />


                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<SignupPage />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route path="/how-it-works" element={<HowItWorks />} />
                        <Route path="/faq" element={<FAQ />} />

                        {/* Guide Pages */}
                        <Route path="/guides/security-checklist" element={<SecurityChecklist />} />
                        <Route path="/guides/dark-web" element={<DarkWebGuide />} />
                        <Route path="/guides/gdpr" element={<GDPRGuide />} />
                        <Route path="/guides/phishing" element={<PhishingGuide />} />
                        <Route path="/guides/passwords" element={<PasswordGuide />} />
                        <Route path="/guides/vpn" element={<VPNGuide />} />
                        <Route path="/guides/backup" element={<BackupGuide />} />

                        {/* Admin Panel (Skyddad) */}
                        <Route
                            path="/admin"
                            element={
                                session?.user?.app_metadata?.role === 'admin' ? (
                                    <AdminPanel session={session} />
                                ) : (
                                    <Navigate to={session ? "/dashboard" : "/login"} replace />
                                )
                            }
                        />

                        {/* Dashboard (Skyddad — kräver onboarding) */}
                        <Route
                            path="/dashboard"
                            element={
                                session ? (
                                    onboardingCompleted || window.location.search.includes('payment=success') || window.location.search.includes('session_id=') ? (
                                        <Dashboard session={session} onLogout={handleLogout} />
                                    ) : (
                                        <Navigate to="/onboarding" replace />
                                    )
                                ) : (
                                    <Navigate to="/login" replace />
                                )
                            }
                        />

                        {/* Onboarding */}
                        <Route
                            path="/onboarding"
                            element={
                                session && onboardingCompleted ? (
                                    <Navigate to="/dashboard" replace />
                                ) : (
                                    <OnboardingWizard />
                                )
                            }
                        />


                        {/* Pricing Redirect (for old Stripe sessions) */}
                        <Route path="/pricing" element={<Navigate to="/signup" replace />} />

                        {/* 404 - Catch all */}
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </Suspense>
            </Router>
        </LanguageProvider>
    );
};

export default App;